import { useState, useEffect } from 'react';
import { Edit2, Save, X, RefreshCw } from 'lucide-react';
import { supabaseClient } from '../../lib/supabase';
import { LoadingScreen } from '../common/LoadingScreen';
import { toast } from '../../lib/toast';

interface APIService {
  id: string;
  name: string;
  service_type: 'dataforseo' | 'candycontent' | 'openai';
  api_key: string;
  config: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function APIServicesPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<APIService[]>([]);
  const [editingService, setEditingService] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseClient
        .from('api_services')
        .select('*')
        .order('name');

      if (error) throw error;
      setServices(data || []);
    } catch (err) {
      console.error('Error loading API services:', err);
      setError(err instanceof Error ? err.message : 'Failed to load API services');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (serviceId: string) => {
    setEditingService(serviceId);
  };

  const handleCancel = () => {
    setEditingService(null);
  };

  const handleSave = async (service: APIService) => {
    try {
      setSaving(true);
      const { error: updateError } = await supabaseClient
        .from('api_services')
        .update({
          api_key: service.api_key,
          config: service.config,
          is_active: service.is_active
        })
        .eq('id', service.id);

      if (updateError) throw updateError;

      setEditingService(null);
      await loadServices();
      toast.success('API service updated successfully');
    } catch (err) {
      console.error('Error updating API service:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update API service');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (service: APIService, key: string, value: string) => {
    const updatedServices = services.map(s => {
      if (s.id === service.id) {
        return {
          ...s,
          config: {
            ...s.config,
            [key]: value
          }
        };
      }
      return s;
    });
    setServices(updatedServices);
  };

  const handleApiKeyChange = (service: APIService, value: string) => {
    const updatedServices = services.map(s => {
      if (s.id === service.id) {
        return {
          ...s,
          api_key: value
        };
      }
      return s;
    });
    setServices(updatedServices);
  };

  const handleToggleActive = async (service: APIService) => {
    try {
      const updatedService = {
        ...service,
        is_active: !service.is_active
      };

      await handleSave(updatedService);
    } catch (err) {
      console.error('Error toggling service status:', err);
      toast.error('Failed to update service status');
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">API Services</h2>

        {error && (
          <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {services.map(service => (
            <div 
              key={service.id}
              className="border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {service.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Type: {service.service_type}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 mr-2">Active</span>
                    <button
                      onClick={() => handleToggleActive(service)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        service.is_active ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          service.is_active ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  {editingService === service.id ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleSave(service)}
                        disabled={saving}
                        className="p-2 text-green-600 hover:text-green-800"
                      >
                        <Save className="h-5 w-5" />
                      </button>
                      <button
                        onClick={handleCancel}
                        className="p-2 text-gray-600 hover:text-gray-800"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEdit(service.id)}
                      className="p-2 text-indigo-600 hover:text-indigo-800"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Key
                  </label>
                  {editingService === service.id ? (
                    <input
                      type="text"
                      value={service.api_key}
                      onChange={(e) => handleApiKeyChange(service, e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  ) : (
                    <div className="bg-gray-50 px-3 py-2 rounded-md text-sm font-mono text-gray-700">
                      {service.api_key}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Configuration
                  </label>
                  <div className="space-y-2">
                    {Object.entries(service.config).map(([key, value]) => (
                      <div key={key} className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500 min-w-[100px]">
                          {key}:
                        </span>
                        {editingService === service.id ? (
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => handleConfigChange(service, key, e.target.value)}
                            className="flex-1 px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        ) : (
                          <span className="text-sm font-mono text-gray-700">
                            {value}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-gray-500">
                  Last updated: {new Date(service.updated_at).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}